import { GraphQL, GraphQLQueryData } from "@rpi/openapi-api";
import { ItemSelectionRow } from "../../OrderItemSelector";
import { getRefundOptionConfig, RefundItemInputType, RefundUIStrategy } from "./refund.constants";

export type OrderForRefund = GraphQLQueryData<GraphQL.OrderQuery>;

export type RefundFormState = {
  reason: GraphQL.RefundReason | undefined;
  reasonNote: string;
  strategy: RefundUIStrategy | undefined;
  items: ItemSelectionRow[];
};

export const INITIAL_REFUND_FORM: RefundFormState = {
  reason: undefined,
  reasonNote: "",
  strategy: undefined,
  items: [],
};

export function buildLocalRefundPreview(
  request: RefundRequest,
  order: Pick<OrderForRefund, "pricing">
): GraphQL.CalculateRefundByQuantityQuery["calculateRefundByQuantity"] | undefined {
  const pricing = order.pricing;
  if (!pricing) return undefined;

  if (request.kind === "fullRefund") {
    return {
      lineItems: [],
      refundSubtotal: (pricing.itemsTotal ?? 0) + (pricing.shippingTotal ?? 0),
      refundTax: pricing.taxesTotal ?? 0,
      totalRefund: pricing.orderTotal ?? 0,
    };
  }
  if (request.kind === "shippingOnly") {
    const shippingTotal = pricing.shippingTotal ?? 0;
    return {
      lineItems: [],
      refundSubtotal: shippingTotal,
      refundTax: 0,
      totalRefund: shippingTotal,
    };
  }
  return undefined;
}

export function requiresReasonNote(reason: GraphQL.RefundReason | undefined): boolean {
  return reason === GraphQL.RefundReason.Other;
}

export function getItemInputType(strategy: RefundUIStrategy | undefined): RefundItemInputType {
  return getRefundOptionConfig(strategy)?.inputType ?? "none";
}

export function requiresItems(strategy: RefundUIStrategy | undefined): boolean {
  const inputType = getItemInputType(strategy);
  return inputType !== "none";
}

/**
 * Discriminated request shape returned by `buildRefundRequest`. Each variant
 * names the backend operation the caller should invoke.
 */
export type RefundRequest =
  | { kind: "byAmount"; input: GraphQL.BraintreeRefundByAmountInput }
  | { kind: "byPercentage"; input: GraphQL.BraintreeRefundByPercentageInput }
  | { kind: "byQuantity"; input: GraphQL.BraintreeRefundByQuantityInput }
  | { kind: "fullRefund"; input: GraphQL.BraintreeRefundInput }
  | { kind: "shippingOnly"; input: GraphQL.BraintreeRefundInput };

export type RefundRequestBuildResult = { ok: true; request: RefundRequest } | { ok: false; reason: string };

type StrategyContext = {
  customerOrderId: string;
  reason: GraphQL.RefundReason;
  reasonNote: string | undefined;
};

type RefundStrategySpec = {
  buildInput: (form: RefundFormState, ctx: StrategyContext) => RefundRequestBuildResult;
};

const REFUND_STRATEGIES: Record<RefundUIStrategy, RefundStrategySpec> = {
  PARTIAL_AMOUNT: { buildInput: buildByAmountRequest },
  PARTIAL_PERCENTAGE: { buildInput: buildByPercentageRequest },
  PARTIAL_QUANTITY: { buildInput: buildByQuantityRequest },
  SHIPPING_ONLY: {
    buildInput: (_form, ctx) => ({
      ok: true,
      request: {
        kind: "shippingOnly",
        input: {
          customerOrderId: ctx.customerOrderId,
          reason: ctx.reason,
          reasonNote: ctx.reasonNote,
          refundStrategy: GraphQL.BraintreeRefundStrategy.ShippingOnly,
        },
      },
    }),
  },
  FULL_REFUND: {
    buildInput: (_form, ctx) => ({
      ok: true,
      request: {
        kind: "fullRefund",
        input: {
          customerOrderId: ctx.customerOrderId,
          reason: ctx.reason,
          reasonNote: ctx.reasonNote,
          refundStrategy: GraphQL.BraintreeRefundStrategy.OrderFullValue,
        },
      },
    }),
  },
};

export function buildRefundRequest(
  form: RefundFormState,
  order: Pick<OrderForRefund, "customerOrderId">
): RefundRequestBuildResult {
  if (!order.customerOrderId) return { ok: false, reason: "Order is missing an identifier." };
  if (!form.reason) return { ok: false, reason: "Please select a reason." };
  if (!form.strategy) return { ok: false, reason: "Please select a refund option." };
  if (requiresReasonNote(form.reason) && form.reasonNote.trim().length === 0) {
    return { ok: false, reason: "Please describe the reason." };
  }

  return REFUND_STRATEGIES[form.strategy].buildInput(form, {
    customerOrderId: order.customerOrderId,
    reason: form.reason,
    reasonNote: form.reasonNote.trim() || undefined,
  });
}

function getRowItemId(row: ItemSelectionRow): number | undefined {
  return row.selectedItem?.id;
}

function buildByAmountRequest(form: RefundFormState, ctx: StrategyContext): RefundRequestBuildResult {
  const amountItems: GraphQL.RefundItemAmount[] = [];
  for (const row of form.items) {
    const id = getRowItemId(row);
    if (id === undefined) return { ok: false, reason: "Please select an item for every row." };
    const amount = Number.parseFloat(row.value);
    if (!Number.isFinite(amount) || amount <= 0) {
      return { ok: false, reason: "Please enter a refund amount for every item." };
    }
    amountItems.push({ id, amount });
  }
  if (amountItems.length === 0) return { ok: false, reason: "Please add at least one item." };

  return {
    ok: true,
    request: {
      kind: "byAmount",
      input: { customerOrderId: ctx.customerOrderId, reason: ctx.reason, reasonNote: ctx.reasonNote, amountItems },
    },
  };
}

function buildByPercentageRequest(form: RefundFormState, ctx: StrategyContext): RefundRequestBuildResult {
  // The user enters whole percent (e.g., "10" for 10%), but the backend's
  // `percentage` field expects a fraction in the 0-1 range — values >1 are
  // rejected with "must be less than or equal to 1". Convert before sending.
  const percentageItems: GraphQL.RefundItemPercentage[] = [];
  for (const row of form.items) {
    const id = getRowItemId(row);
    if (id === undefined) return { ok: false, reason: "Please select an item for every row." };
    const userPercent = Number.parseFloat(row.value);
    if (!Number.isFinite(userPercent) || userPercent <= 0 || userPercent > 100) {
      return { ok: false, reason: "Please enter a percentage between 1 and 100 for every item." };
    }
    percentageItems.push({ id, percentage: userPercent / 100 });
  }
  if (percentageItems.length === 0) return { ok: false, reason: "Please add at least one item." };

  return {
    ok: true,
    request: {
      kind: "byPercentage",
      input: { customerOrderId: ctx.customerOrderId, reason: ctx.reason, reasonNote: ctx.reasonNote, percentageItems },
    },
  };
}

function buildByQuantityRequest(form: RefundFormState, ctx: StrategyContext): RefundRequestBuildResult {
  const quantityItems: GraphQL.RefundItemQuantity[] = [];
  for (const row of form.items) {
    const id = getRowItemId(row);
    if (id === undefined) return { ok: false, reason: "Please select an item for every row." };
    const quantity = Number.parseInt(row.value, 10);
    if (!Number.isInteger(quantity) || quantity <= 0) {
      return { ok: false, reason: "Please enter a quantity for every item." };
    }
    quantityItems.push({ id, quantity });
  }
  if (quantityItems.length === 0) return { ok: false, reason: "Please add at least one item." };

  return {
    ok: true,
    request: {
      kind: "byQuantity",
      input: { customerOrderId: ctx.customerOrderId, reason: ctx.reason, reasonNote: ctx.reasonNote, quantityItems },
    },
  };
}
